import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';

import { calendar } from 'assets';
import { DatePicker } from 'components/UI';
import { Dropdown } from 'components/Forms';
import { afterToday } from 'utils/Time';

interface IDateRangeSelectorProps {
  handleDispatchRange: ({ startDate, endDate }: { startDate: DateTime; endDate: DateTime }) => any;
  handleDispatchPeriod: (period: IPeriod) => any;
  graphPeriod: IPeriod;
  startDate: DateTime;
  endDate: DateTime;
  dataTestPrefix?: string;
  limitMaxDateToday?: boolean;
}

const DateRangeSelector = ({
  handleDispatchRange,
  handleDispatchPeriod,
  graphPeriod,
  startDate,
  endDate,
  dataTestPrefix,
  limitMaxDateToday = true
}: IDateRangeSelectorProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const periodsOption: IPeriodOption[] = [
    {
      label: intl.formatMessage({ id: 'last7daysKey' }),
      value: '7d'
    },
    {
      label: intl.formatMessage({ id: 'last14daysKey' }),
      value: '14d'
    },
    {
      label: intl.formatMessage({ id: 'last30daysKey' }),
      value: '30d'
    },
    {
      label: intl.formatMessage({ id: 'last90daysKey' }),
      value: '90d'
    },
    {
      label: intl.formatMessage({ id: 'monthlyKey' }),
      value: 'monthly'
    },
    {
      label: intl.formatMessage({ id: 'yearlyKey' }),
      value: 'yearly'
    },
    {
      label: intl.formatMessage({ id: 'customDateRangeKey' }),
      value: 'custom_range'
    }
  ];

  const handleDateStart = (value: Date) => {
    switch (graphPeriod) {
      case 'daily':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.fromJSDate(value),
            endDate: DateTime.fromJSDate(value)
          })
        );
        break;
      case 'monthly':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.fromJSDate(value).startOf('month'),
            endDate: limitMaxDateToday
              ? afterToday(value, 'month')
              : DateTime.fromJSDate(value).endOf('month')
          })
        );
        break;
      case 'yearly':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.fromJSDate(value).startOf('year'),
            endDate: limitMaxDateToday
              ? afterToday(value, 'year')
              : DateTime.fromJSDate(value).endOf('year')
          })
        );
        break;
      case 'custom_range':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.fromJSDate(value),
            endDate
          })
        );
        break;
    }
  };

  const handlePeriod = (option: IPeriodOption) => {
    switch (option.value) {
      case '7d':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now().minus({ days: 6 }),
            endDate: DateTime.now()
          })
        );
        break;
      case '14d':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now().minus({ days: 13 }),
            endDate: DateTime.now()
          })
        );
        break;
      case '30d':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now().minus({ days: 29 }),
            endDate: DateTime.now()
          })
        );
        break;
      case '90d':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now().minus({ days: 89 }),
            endDate: DateTime.now()
          })
        );
        break;
      case 'daily':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now(),
            endDate: DateTime.now()
          })
        );
        break;
      case 'monthly':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now().startOf('month'),
            endDate: limitMaxDateToday ? DateTime.now() : DateTime.now().endOf('month')
          })
        );
        break;
      case 'yearly':
        dispatch(
          handleDispatchRange({
            startDate: DateTime.now().startOf('year'),
            endDate: limitMaxDateToday ? DateTime.now() : DateTime.now().endOf('year')
          })
        );
        break;
    }
    dispatch(handleDispatchPeriod(option.value));
  };

  const handleDateEnd = (value: Date) => {
    dispatch(
      handleDispatchRange({
        startDate,
        endDate: DateTime.fromJSDate(value)
      })
    );
  };

  const handleMaxDate = () => {
    if (!limitMaxDateToday) return undefined;
    if (graphPeriod === 'yearly') {
      return DateTime.now().endOf('year').toJSDate();
    }
    if (graphPeriod === 'custom_range' && endDate) {
      return endDate.toJSDate();
    }
    return DateTime.now().toJSDate();
  };

  return (
    <>
      <Dropdown
        dataTestId={`${dataTestPrefix}-date-range-selector`}
        icon={calendar}
        isSearchable={false}
        placeholder={intl.formatMessage({ id: 'dateRangeKey' })}
        options={periodsOption}
        onChange={handlePeriod}
        value={periodsOption.find((item) => item.value === graphPeriod)}
        variant="transparent"
        className="flex-grow-1 text-center"
      />
      {graphPeriod && !['30d', '90d', '14d', '7d'].includes(graphPeriod) && (
        <DatePicker
          dataTestId={`${dataTestPrefix}-date-range-start`}
          className="ms-2"
          title="start"
          selected={startDate.toJSDate()}
          onChange={(date) => date && handleDateStart(date)}
          dateFormat={
            graphPeriod === 'monthly' ? 'MMMM yyyy' : graphPeriod === 'yearly' ? 'yyyy' : undefined
          }
          showMonthYearPicker={graphPeriod === 'monthly'}
          showFullMonthYearPicker={graphPeriod === 'monthly'}
          showYearPicker={graphPeriod === 'yearly'}
          maxDate={handleMaxDate()}
          calendarClassName="me-4"
        />
      )}
      {graphPeriod === 'custom_range' && (
        <DatePicker
          dataTestId={`${dataTestPrefix}-date-range-end`}
          className="ms-2"
          title="end"
          selected={endDate.toJSDate()}
          onChange={(date) => date && handleDateEnd(date)}
          selectsEnd
          startDate={startDate.toJSDate()}
          maxDate={limitMaxDateToday ? DateTime.now().toJSDate() : undefined}
          endDate={endDate.toJSDate()}
          minDate={startDate.toJSDate()}
          calendarClassName="me-4"
        />
      )}
    </>
  );
};

export default DateRangeSelector;
